import { useEffect, useState } from 'react';

// Material UI
import { Card, CardContent, Grid, Stack, Button, Icon, Box, IconButton, Alert } from '@mui/material';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

// Componentes
import TableDragDrop from '@components/Tables/TableDragDrop';
import LoadingForm from '@components/LinearProgress/LoadingForm';
import RegisterForm from '@components/Foca/Register_form';
import { useCatalogs } from '@hooks/useCatalogs';
import { convertBase64 } from '@utils/Utilities';

import serv from '@services/Estructura';
import { Swal, SwalDelete } from '@utils/alerts';

import { camposAEliminar, columns } from '@data/constants/Foca';

const ListEstructura = ({ type, permissions }) => {
  const [loading, setloading] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [data, setdata] = useState([]);
  const [newData, setNewData] = useState([]);
  const [oldData, setOldData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [params, setParams] = useState({});

  const catalogsOptions = [{ id: 'municipios' }];

  const { catalogs, load } = useCatalogs({
    catalogsOptions: catalogsOptions,
    putDefaultOption: false,
  });

  const WhiteTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#fff',
      color: '#000',
      boxShadow: '0px 0px 10px rgba(0,0,0,0.1)',
      fontSize: 14,
    },
  });

  const ApiResponse = async () => {
    try {
      setloading(true);
      const result = await serv.getEstructuraCapacitadores({ type: type });
      const { results, response, message } = result;
      if (results) {
        const format = response.data.map((item) => {
          return {
            ...item,
            Acciones: (
              <Box sx={{ display: 'flex', justifyContent: 'left', gap: 1 }}>
                <>
                  {permissions.edit && (
                    <IconButton
                      aria-label="edit"
                      color="success"
                      onClick={() => {
                        handleEdit(item);
                      }}
                    >
                      <Icon>edit</Icon>
                    </IconButton>
                  )}
                  {permissions.delete && (
                    <IconButton
                      aria-label="delete"
                      sx={{ color: '#ca0b0b' }}
                      onClick={() => {
                        handleDelete(item);
                      }}
                    >
                      <Icon>delete</Icon>
                    </IconButton>
                  )}
                </>
              </Box>
            ),
            image: (
              <WhiteTooltip
                title={
                  <img src={item.imagePath} alt={item.imageName} style={{ maxWidth: 200, maxHeight: 200 }} />
                }
                placement="right"
              >
                <a href={item.imagePath} target="_blank" rel="noopener noreferrer">
                  {item.imageName}
                </a>
              </WhiteTooltip>
            ),
          };
        });
        setdata(format);
        setOldData(format);
      } else throw new Error(message);
    } catch (error) {
      setdata([]);
      Swal.fire({
        title: error.message,
        icon: 'warning',
        customClass: { container: 'modal-alert' },
      });
    } finally {
      setloading(false);
    }
  };

  const ApiResponseUpdate = async (params) => {
    try {
      setLoadingUpdate(true);
      const result = await serv.updateEstructuraCapacitadores(params);
      const { results, message } = result;
      if (results) {
        Swal.fire({
          title: message,
          icon: 'success',
          customClass: { container: 'modal-alert' },
        });
        setdata([]);
        setNewData([]);
        setOldData([]);
        ApiResponse();
      } else {
        throw new Error(message);
      }
    } catch (error) {
      Swal.fire({
        title: error.message,
        icon: 'warning',
        customClass: { container: 'modal-alert' },
      });
    } finally {
      setLoadingUpdate(false);
    }
  };

  const ApiResponseDelete = async (params) => {
    try {
      setLoadingUpdate(true);
      const result = await serv.deleteEstructura(params);
      const { results, message } = result;
      if (results) {
        Swal.fire({
          title: message,
          icon: 'success',
          customClass: { container: 'modal-alert' },
        });
        ApiResponse();
      } else {
        throw new Error(message);
      }
    } catch (error) {
      Swal.fire({
        title: error.message,
        icon: 'warning',
        customClass: { container: 'modal-alert' },
      });
    } finally {
      setLoadingUpdate(false);
    }
  };

  const handleUpdateUser = async (value) => {
    try {
      setLoadingUpdate(true);
      let file = value.files[0]?.file;

      if (file) {
        if (!isBase64(file)) {
          if (value.comparar !== file) {
            const base64File = await convertBase64(file);
            value.files[0].file = base64File;
          } else {
            ['comparar', 'files'].forEach((prop) => delete value[prop]);
          }
        } 
      }

      ['imageName', 'comparar'].forEach((prop) => {
        if (value.hasOwnProperty(prop)) {
          delete value[prop];
        }
      });

      Object.keys(value).forEach((key) => {
        if (value[key] === '') {
          value[key] = null;
        }
      });

      const result = await serv.updateUserEstructura(value);
      const { results, message } = result;
      if (results) {
        await ApiResponse();
        Swal.fire({
          title: message,
          icon: 'success',
          customClass: {
            container: 'modal-alert',
          },
        });
        setOpenModal(false);
      } else {
        throw new Error(message);
      }
    } catch (error) {
      Swal.fire({
        title: error.message,
        icon: 'warning',
        customClass: {
          container: 'modal-alert',
        },
      });
    } finally {
      setLoadingUpdate(false);
    }
  };

  const isBase64 = (str) => {
    const base64Regex = /^(data:image\/[a-zA-Z]+;base64,)?[A-Za-z0-9+/=]+$/;
    return base64Regex.test(str);
  };

  const handleRowChange = (updatedData, dataToUpdate) => {
    setdata(updatedData);
    const changedRows = dataToUpdate.filter((item) => item.TableOrder !== item.newRow);
    setNewData(changedRows);
  };

  const handleClear = () => {
    setdata(oldData);
    setNewData([]);
  };

  const handleSave = () => {
    let params = {
      type: type,
      data: newData.map((item) => {
        let filteredItem = { ...item };
        camposAEliminar.forEach((prop) => {
          if (filteredItem.hasOwnProperty(prop)) {
            delete filteredItem[prop];
          }
        });
        return filteredItem;
      }),
    };

    ApiResponseUpdate(params);
  };

  const handleEdit = (value) => {
    setOpenModal(true);
    setParams(value);
  };

  const handleDelete = (value) => {
    SwalDelete({
      title: 'Eliminar Capacitador',
      text: `¿Estás seguro de querer eliminar a:   ${value.Nombre}?`,
      buttonText: 'Eliminar',
      onDelete: () => ApiResponseDelete({ type: type, id: value.id }),
    });
  };

  useEffect(() => {
    ApiResponse();
    //eslint-disable-next-line
  }, []);

  return (
    <Card className="card-primary">
      <CardContent>
        <LoadingForm
          loadinMessage={'Cargando...'}
          isLoading={loadingUpdate}
          isOpen={loadingUpdate}
          setIsOpen={() => {}}
        />
        <Alert severity="info" sx={{ marginBottom: 2 }}>
          Puedes arrastrar las filas de la tabla para cambiar el orden en que se muestran. Simplemente
          selecciona una fila y muévela hacia la posición deseada.
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableDragDrop
              columns={columns(permissions, type)}
              data={data}
              onRowChange={handleRowChange}
              loading={loading}
              maxHeight={750}
              stickyHeader
            />
          </Grid>
        </Grid>

        {newData.length > 0 && (
          <Stack direction="row" spacing={1} marginTop={2} justifyContent="center">
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<Icon>clear</Icon>}
              onClick={() => handleClear()}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<Icon>save</Icon>}
              onClick={() => handleSave()}
            >
              Guardar
            </Button>
          </Stack>
        )}
        {openModal && (
          <RegisterForm
            setModalOpen={setOpenModal}
            modalOpen={openModal}
            params={params}
            setData={handleUpdateUser}
            showInut={false}
            catalogs={catalogs}
            load={load}
            type={type}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ListEstructura;
